import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from 'store/middleware/api/api';
import { costTrackerFields } from 'src/enums/cost-tracker';
import { IAddCostTrackerItem, ICostTrackerInitial } from 'src/common/interfaces/cost-tracker.interfaces';
import { sum } from 'src/views/cost-tracker/cost-tracker-common/utils/utils';

const initialState: ICostTrackerInitial = {
  isAdding: false,
  isFetching: false,
  list: [],
  listByCosts: {},
  disabledFields: [],
};

const costTracker = createSlice({
  name: 'costTracker',
  initialState,
  reducers: {
    itemsRequested: (costTracker) => {
      costTracker.isFetching = true;
    },
    itemsFetched: (costTracker, action) => {
      costTracker.isFetching = false;
      costTracker.list = action.payload.costTrackerItems;
      costTracker.listByCosts = action.payload.costTrackerByCostItems;
    },
    itemsRequestedFailed: (costTracker) => {
      costTracker.isFetching = false;
    },
    addTaskRequested: (costTracker) => {
      costTracker.isAdding = true;
    },
    taskAdded: (costTracker, action) => {
      const { costTrackerItems } = action.payload;
      costTracker.list = costTrackerItems;
      costTracker.isAdding = false;
    },
    taskRemoved: (costTracker, action) => {
      const { costTrackerItems } = action.payload;
      costTracker.list = costTrackerItems;
      costTracker.isAdding = false;
    },
    taskReodered: (costTracker, action) => {
      const { costTrackerItems } = action.payload;
      costTracker.list = costTrackerItems;
      costTracker.isAdding = false;
    },
    updateTaskRequested: (costTracker) => {
      costTracker.isAdding = true;
    },
    taskUpdated: (costTracker) => {
      costTracker.isAdding = false;
    },
    listCleared: (costTracker) => {
      costTracker.list = [];
      costTracker.listByCosts = {};
    },
    cellValueUpdated: (costTracker, action) => {
      const { sectionId, operationId, costTrackerItemId, value, field } = action.payload;
      const costTrackerSectionIdx = costTracker.list.findIndex((section) => section.sectionId === sectionId);
      const sectionOperationIndex = costTracker.list[costTrackerSectionIdx].operations.findIndex(
        (item) => item.originId === operationId,
      );
      const costTrackerItemIdx = costTracker.listByCosts[operationId].findIndex(
        (cost) => cost.costTrackerItemId === costTrackerItemId,
      );
      const updatedCostItem = { ...costTracker.listByCosts[operationId][costTrackerItemIdx], [field]: value };
      costTracker.listByCosts[operationId][costTrackerItemIdx] = updatedCostItem;
      if (field === costTrackerFields.COST_ACTUAL) {
        costTracker.list[costTrackerSectionIdx].operations[sectionOperationIndex][costTrackerFields.COST_ACTUAL] = sum(
          costTracker.listByCosts[operationId],
          costTrackerFields.COST_ACTUAL,
        );
      }
    },
    disabledFieldsUpdated: (costTracker, action) => {
      costTracker.disabledFields = action.payload;
    },
  },
});

export const {
  itemsRequested,
  itemsFetched,
  addTaskRequested,
  taskAdded,
  taskRemoved,
  taskUpdated,
  listCleared,
  updateTaskRequested,
  cellValueUpdated,
  // cellValueByCostUpdated,
  disabledFieldsUpdated,
  itemsRequestedFailed,
  taskReodered,
} = costTracker.actions;
export default costTracker.reducer;

export const getCostTrackerItems = (projectId: string) =>
  apiCallBegan({
    url: `/api/cost-tracker/${projectId}`,
    method: 'GET',
    onStart: itemsRequested.type,
    onSuccess: (response) => ({
      type: itemsFetched.type,
      payload: response,
    }),
    onError: itemsRequestedFailed.type,
  });

export const addCostTrackerItem = (data: IAddCostTrackerItem) =>
  apiCallBegan({
    url: '/api/cost-tracker/operation',
    method: 'POST',
    data,
    onStart: addTaskRequested.type,
    onSuccess: (response) => ({
      type: taskAdded.type,
      payload: response,
    }),
  });

export const deleteCostTrackerItem = (projectId: string, costTrackerId: string, costTrackerItemId: string) =>
  apiCallBegan({
    url: `/api/cost-tracker/task/${projectId}`,
    method: 'DELETE',
    data: { costTrackerId, costTrackerItemId },
    onSuccess: (response) => ({
      type: taskRemoved.type,
      payload: response,
    }),
  });

export const updateCostTrackerItem = (
  costTrackerItemId: string,
  data: { field: string; value: string | number | null },
) =>
  apiCallBegan({
    url: `/api/cost-tracker/operation/${costTrackerItemId}`,
    method: 'PUT',
    data,
    onStart: updateTaskRequested.type,
    onSuccess: (response) => ({
      type: taskUpdated.type,
      payload: response,
    }),
  });

export const updateCostTrackerByCostItem = (
  costTrackerItemId: string,
  data: { field: string; value: string | number | null },
) =>
  apiCallBegan({
    url: `/api/cost-tracker/cost/${costTrackerItemId}`,
    method: 'PUT',
    data,
    onStart: updateTaskRequested.type,
    onSuccess: (response) => ({
      type: taskUpdated.type,
      payload: response,
    }),
  });

export const reorderCostTrackerList = (projectId: string, data: { fromItemId: string; toItemId: string }) =>
  apiCallBegan({
    url: `/api/cost-tracker/reorder/${projectId}`,
    method: 'PUT',
    data,
    onStart: updateTaskRequested.type,
    onSuccess: (response) => ({
      type: taskReodered.type,
      payload: response,
    }),
  });
